import type { ReactNode } from 'react';
import type { GetRequestForQuoteWithQuotesQuery } from '~/graphql/graphql';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import { QuoteComparisonList } from '~/pages/rfq-view/sections/QuoteComparisonList';
import type { QuoteNegotiationContexts } from '~/pages/rfq-view/sections/quoteNegotiation';
import type { ComparedTally } from './comparedTally';
import { ComparisonGroup } from './ComparisonGroup';
import { useComparisonHeadings } from './useComparisonHeadings';

type ComparedQuote =
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote'][number];

export type QuoteComparison = {
  key: string;
  tally: ComparedTally | null;
  requestForQuote: NonNullable<
    GetRequestForQuoteWithQuotesQuery['requestForQuote']
  >;
  quotes: ComparedQuote[];
  archivedQuotes: ComparedQuote[];
  groups: ProductGroup[];
};

type QuoteComparisonSectionProps<Comparison extends QuoteComparison> = {
  comparisons: Comparison[];
  negotiationByRequestForQuoteId: QuoteNegotiationContexts;
  notices?: ReactNode;
  renderGroupHeader?: (comparison: Comparison) => ReactNode;
  emptyMessage?: string;
  testId: string;
};

export function QuoteComparisonSection<Comparison extends QuoteComparison>({
  comparisons,
  negotiationByRequestForQuoteId,
  notices,
  renderGroupHeader,
  emptyMessage,
  testId,
}: Readonly<QuoteComparisonSectionProps<Comparison>>) {
  return (
    <section className="flex flex-col gap-6" data-testid={testId}>
      {notices}
      {comparisons.length === 0 ? (
        emptyMessage !== undefined && (
          <p className="text-sm text-neut-sec" data-testid={`${testId}-empty`}>
            {emptyMessage}
          </p>
        )
      ) : (
        <GroupedComparisons
          comparisons={comparisons}
          negotiationByRequestForQuoteId={negotiationByRequestForQuoteId}
          renderGroupHeader={renderGroupHeader}
        />
      )}
    </section>
  );
}

function GroupedComparisons<Comparison extends QuoteComparison>({
  comparisons,
  negotiationByRequestForQuoteId,
  renderGroupHeader,
}: Readonly<
  Pick<
    QuoteComparisonSectionProps<Comparison>,
    'comparisons' | 'negotiationByRequestForQuoteId' | 'renderGroupHeader'
  >
>) {
  const headings = useComparisonHeadings(comparisons);
  return comparisons.map((comparison, index) => (
    <ComparisonGroup
      key={comparison.key}
      heading={headings[index]}
      testId={comparison.key}
    >
      {renderGroupHeader && renderGroupHeader(comparison)}
      <QuoteComparisonList
        requestForQuote={comparison.requestForQuote}
        quotes={comparison.quotes}
        archivedQuotes={comparison.archivedQuotes}
        groups={comparison.groups}
        negotiationByRequestForQuoteId={negotiationByRequestForQuoteId}
      />
    </ComparisonGroup>
  ));
}
